import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Formik } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import {
  Alert,
  Col,
  DatePicker,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Table,
  notification,
} from "antd";
import NumberFormat from "react-number-format";

import ButtonStandard from "../../utils/button";
import { Body, Container, Footer, ResultPanel, SectionTitle } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import { resolveProductionOrderError } from "../../../utils/productionOrderErrors";
import { getSupplementPageableIndex } from "../../../services/supplementService";
import { index as indexWagons } from "../../../services/wagonService";
import { findFeedFactoryParameterData } from "../../../services/feedFactoryParameterService";
import {
  calculateProductionOrder,
  checkProductionOrderRecipe,
  saveProductionOrder,
  updateProductionOrder,
} from "../../../services/productionOrderService";

const INITIAL_FORM = {
  id: null,
  supplementId: null,
  wagonId: null,
  productionDate: null,
  targetDryMatterKg: null,
  useFullMixerCapacity: null,
  observations: null,
};

const INVALIDATING_FIELDS = [
  "supplementId",
  "wagonId",
  "targetDryMatterKg",
  "useFullMixerCapacity",
];

const DrawerProductionOrder = ({
  visible,
  order,
  readOnly,
  onClose,
  onSave,
}) => {
  const formikRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [recipes, setRecipes] = useState([]);
  const [wagons, setWagons] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState({});
  const [recipeReadiness, setRecipeReadiness] = useState(null);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState({});

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const validationSchema = Yup.object().shape({
    supplementId: Yup.string().required(),
    wagonId: Yup.string().required(),
    productionDate: Yup.string().required(),
    targetDryMatterKg: Yup.number().moreThan(0).required(),
  });

  const fetchOptions = useCallback(async () => {
    try {
      const [
        {
          data: { results: supplementResults },
        },
        {
          data: { results: wagonResults },
        },
        {
          data: { results: parameterResults },
        },
      ] = await Promise.all([
        getSupplementPageableIndex({
          groupId,
          farmId,
          filters: "status IN ('A', 'F');supplement_formulated = true",
          page: 0,
          size: 100,
        }),
        indexWagons({ groupId, farmId, size: 1000 }),
        findFeedFactoryParameterData({ groupId, farmId }),
      ]);

      setRecipes(
        (supplementResults?.content ?? []).filter(
          (supplement) => supplement.supplementFormulated === true,
        ),
      );
      setWagons(
        (wagonResults?.content ?? wagonResults ?? []).filter(
          (wagon) => wagon.status === "Active",
        ),
      );

      return {
        defaultWagonId: parameterResults?.defaultWagonId ?? null,
        useFullMixerCapacity: parameterResults?.useFullMixerCapacity ?? null,
      };
    } catch (error) {
      console.error(error);
      notification.error({
        message: translation.productionOrder.messages.errorLoad,
      });
      return { defaultWagonId: null, useFullMixerCapacity: null };
    }
  }, [groupId, farmId, translation]);

  const handleDrawerOpen = useCallback(
    async (isVisible) => {
      if (isVisible !== true) {
        setForm(INITIAL_FORM);
        setCalculation(null);
        setSelectedBatches({});
        setRecipeReadiness(null);
        return;
      }

      setIsLoading(true);
      const parameter = await fetchOptions();

      if (order != null) {
        const values = {
          id: order.id,
          supplementId: order.supplementId,
          wagonId: order.wagonId,
          productionDate: order.productionDate,
          targetDryMatterKg: order.targetDryMatterKg,
          useFullMixerCapacity:
            order.useFullMixerCapacity ?? parameter.useFullMixerCapacity,
          observations: order.observations,
        };
        setForm(values);
        formikRef.current.setValues(values);
        setSelectedBatches(
          (order.items ?? []).reduce(
            (accumulator, item) => ({
              ...accumulator,
              [item.supplementId]: item.stockItemBatchId,
            }),
            {},
          ),
        );
      } else {
        const values = {
          ...INITIAL_FORM,
          wagonId: parameter.defaultWagonId,
          useFullMixerCapacity: parameter.useFullMixerCapacity,
        };
        setForm(values);
        formikRef.current.setValues(values);
      }

      setIsLoading(false);
    },
    [fetchOptions, order],
  );

  useEffect(() => {
    if (!visible) {
      setCalculation(null);
      setSelectedBatches({});
      setRecipeReadiness(null);
    }
  }, [visible]);

  const checkRecipe = useCallback(
    async (supplementId) => {
      if (supplementId == null) {
        setRecipeReadiness(null);
        return;
      }

      try {
        const {
          data: { results },
        } = await checkProductionOrderRecipe({ groupId, farmId, supplementId });
        setRecipeReadiness(results);
      } catch (error) {
        console.error(error);
        setRecipeReadiness(null);
      }
    },
    [groupId, farmId],
  );

  const handleChangeField = useCallback(
    (field, value, setFieldValue) => {
      setFieldValue(field, value);

      if (INVALIDATING_FIELDS.includes(field)) {
        setCalculation(null);
      }

      if (field === "supplementId") {
        setSelectedBatches({});
        setRecipeReadiness(null);
        checkRecipe(value);
      }
    },
    [checkRecipe],
  );

  const buildCalculationBody = useCallback(
    (values, batches) => ({
      supplementId: values.supplementId,
      wagonId: values.wagonId,
      targetDryMatterKg: values.targetDryMatterKg,
      useFullMixerCapacity: values.useFullMixerCapacity,
      productionDate: values.productionDate
        ? moment(values.productionDate).format("YYYY-MM-DD")
        : null,
      items: Object.entries(batches)
        .filter(([, batchId]) => batchId != null)
        .map(([supplementId, batchId]) => ({
          supplementId,
          stockItemBatchId: batchId,
        })),
    }),
    [],
  );

  const runCalculation = useCallback(
    async (values, batches) => {
      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await calculateProductionOrder({
          groupId,
          farmId,
          body: buildCalculationBody(values, batches),
        });
        setCalculation(results);
        setSelectedBatches(
          (results?.items ?? []).reduce(
            (accumulator, item) => ({
              ...accumulator,
              [item.supplementId]: item.stockItemBatchId ?? null,
            }),
            {},
          ),
        );
      } catch (error) {
        setCalculation(null);
        notification.error({
          message: resolveProductionOrderError(
            error,
            translation,
            translation.productionOrder.messages.errorCalculate,
          ),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, buildCalculationBody, translation],
  );

  const handleCalculate = useCallback(
    (values) => runCalculation(values, selectedBatches),
    [runCalculation, selectedBatches],
  );

  const handleChangeBatch = useCallback(
    (supplementId, batchId, values) => {
      const nextBatches = {
        ...selectedBatches,
        [supplementId]: batchId ?? null,
      };
      setSelectedBatches(nextBatches);
      runCalculation(values, nextBatches);
    },
    [selectedBatches, runCalculation],
  );

  const handleSubmitForm = useCallback(
    async (values) => {
      if (calculation == null) {
        notification.error({
          message: translation.productionOrder.form.mustCalculate,
        });
        return;
      }

      setIsLoading(true);
      try {
        const body = {
          ...buildCalculationBody(values, selectedBatches),
          productionDate: moment(values.productionDate).format("YYYY-MM-DD"),
          observations: values.observations,
        };

        if (values.id != null) {
          await updateProductionOrder({
            groupId,
            farmId,
            id: values.id,
            body,
          });
        } else {
          await saveProductionOrder({ groupId, farmId, body });
        }

        notification.success({
          message: translation.productionOrder.messages.successSave,
        });
        onSave();
      } catch (error) {
        notification.error({
          message: resolveProductionOrderError(
            error,
            translation,
            translation.productionOrder.messages.errorSave,
          ),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildCalculationBody,
      calculation,
      farmId,
      groupId,
      onSave,
      selectedBatches,
      translation,
    ],
  );

  useEffect(() => {
    setAcknowledgedWarnings({});
  }, [calculation]);

  const warningKey = (warning, index) => `${warning.type}-${index}`;

  const warnings = calculation?.warnings ?? [];

  const pendingWarnings = warnings.filter(
    (warning, index) => !acknowledgedWarnings[warningKey(warning, index)],
  ).length;

  const handleAcknowledgeWarning = (warning, index) => {
    setAcknowledgedWarnings((current) => ({
      ...current,
      [warningKey(warning, index)]: true,
    }));
  };

  const warningMessage = (warning) => {
    if (warning.type === "MissingStock") {
      return translation.productionOrder.warnings.missingStock
        .replace("{{name}}", warning.name)
        .replace("{{required}}", numberMask(warning.requiredQuantity))
        .replace("{{available}}", numberMask(warning.availableQuantity));
    }

    if (warning.type === "PartialBatch") {
      return translation.productionOrder.warnings.partialBatch
        .replace("{{batch}}", numberMask(warning.batchDryMatterKg))
        .replace("{{missing}}", numberMask(warning.missingDryMatterKg))
        .replace(
          "{{suggested}}",
          numberMask(warning.suggestedTargetDryMatterKg),
        );
    }

    if (warning.type === "TargetAdjusted") {
      return translation.productionOrder.warnings.targetAdjusted
        .replace("{{informed}}", numberMask(warning.informedTargetDryMatterKg))
        .replace(
          "{{adjusted}}",
          numberMask(warning.suggestedTargetDryMatterKg),
        );
    }

    if (warning.type === "MissingCost") {
      return translation.productionOrder.warnings.missingCost.replace(
        "{{name}}",
        warning.name,
      );
    }

    return translation.productionOrder.warnings.expiredBatch
      .replace("{{batch}}", warning.batchNumber)
      .replace("{{name}}", warning.name)
      .replace(
        "{{expiration}}",
        warning.expiration
          ? moment(warning.expiration).format("DD/MM/YYYY")
          : "",
      );
  };

  const batchLabel = (option) =>
    option.expiration
      ? `${option.batchNumber} (${
          translation.productionOrder.form.expiresAt
        } ${moment(option.expiration).format("DD/MM/YYYY")})`
      : option.batchNumber;

  const resultItem = (label, value) => (
    <Col xs={12} sm={12} md={8} lg={6} xl={6}>
      <div className="resultItem">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
    </Col>
  );

  const drawerTitle = readOnly
    ? translation.productionOrder.form.titleDetails
    : order != null
      ? translation.productionOrder.form.titleEdit
      : translation.productionOrder.form.titleCreateNew;

  const items = calculation?.items ?? order?.items ?? [];
  const summary = calculation ?? (readOnly ? order : null);

  return (
    <Container
      title={drawerTitle}
      width={900}
      onClose={onClose}
      maskClosable={false}
      visible={visible}
      afterVisibleChange={handleDrawerOpen}
    >
      <Formik
        ref={formikRef}
        enableReinitialize={true}
        initialValues={form}
        onSubmit={handleSubmitForm}
        validationSchema={validationSchema}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <Spin spinning={isLoading}>
            <form onSubmit={handleSubmit} autoComplete="off">
              <Body>
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}
                <Row type="flex" gutter={8}>
                  <Col xs={24} sm={24} md={10} lg={10} xl={10}>
                    <Row>
                      <label
                        className={
                          errors.supplementId && submitCount > 0 ? "error" : ""
                        }
                      >
                        {translation.productionOrder.form.supplement}*
                      </label>
                    </Row>
                    <Row>
                      <Select
                        style={{ width: "100%" }}
                        disabled={readOnly}
                        value={values.supplementId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value) =>
                          handleChangeField(
                            "supplementId",
                            value,
                            setFieldValue,
                          )
                        }
                      >
                        {recipes.map((recipe) => (
                          <Select.Option key={recipe.id} value={recipe.id}>
                            {recipe.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                    <Row>
                      <label
                        className={
                          errors.wagonId && submitCount > 0 ? "error" : ""
                        }
                      >
                        {translation.productionOrder.form.wagon}*
                      </label>
                    </Row>
                    <Row>
                      <Select
                        style={{ width: "100%" }}
                        disabled={readOnly}
                        value={values.wagonId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) =>
                          handleChangeField("wagonId", value, setFieldValue)
                        }
                      >
                        {wagons.map((wagon) => (
                          <Select.Option key={wagon.id} value={wagon.id}>
                            {wagon.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                    <Row>
                      <label
                        className={
                          errors.productionDate && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.productionOrder.form.productionDate}*
                      </label>
                    </Row>
                    <Row>
                      <DatePicker
                        style={{ width: "100%" }}
                        disabled={readOnly}
                        format="DD/MM/YYYY"
                        value={
                          values.productionDate
                            ? moment(values.productionDate)
                            : undefined
                        }
                        placeholder={translation.defaultDatePickerPlaceholder}
                        onChange={(date) =>
                          handleChangeField(
                            "productionDate",
                            date,
                            setFieldValue,
                          )
                        }
                      />
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                    <Row>
                      <label
                        className={
                          errors.targetDryMatterKg && submitCount > 0
                            ? "error"
                            : ""
                        }
                      >
                        {translation.productionOrder.form.targetDryMatterKg}*
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        value={values.targetDryMatterKg ?? ""}
                        disabled={readOnly}
                        decimalScale={2}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        allowNegative={false}
                        onValueChange={({ floatValue }) =>
                          handleChangeField(
                            "targetDryMatterKg",
                            floatValue == null ? null : floatValue,
                            setFieldValue,
                          )
                        }
                      />
                    </Row>
                  </Col>
                </Row>

                <Row type="flex" gutter={8}>
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Row>
                      <label>
                        {translation.productionOrder.form.useFullMixerCapacity}
                      </label>
                    </Row>
                    <Row type="flex" gutter={8} align="middle">
                      <Col>
                        <Radio
                          disabled={readOnly}
                          checked={values.useFullMixerCapacity === true}
                          onChange={() =>
                            handleChangeField(
                              "useFullMixerCapacity",
                              true,
                              setFieldValue,
                            )
                          }
                        >
                          {translation.buttons.yes}
                        </Radio>
                      </Col>
                      <Col>
                        <Radio
                          disabled={readOnly}
                          checked={values.useFullMixerCapacity === false}
                          onChange={() =>
                            handleChangeField(
                              "useFullMixerCapacity",
                              false,
                              setFieldValue,
                            )
                          }
                        >
                          {translation.buttons.no}
                        </Radio>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row type="flex" gutter={8}>
                  <Col span={24}>
                    <Row>
                      <label>
                        {translation.productionOrder.form.observations}
                      </label>
                    </Row>
                    <Row>
                      <Input.TextArea
                        rows={2}
                        disabled={readOnly}
                        value={values.observations ?? ""}
                        onChange={(event) =>
                          setFieldValue("observations", event.target.value)
                        }
                      />
                    </Row>
                  </Col>
                </Row>

                {!readOnly && (
                  <Row type="flex" justify="end" style={{ marginTop: 12 }}>
                    <Col>
                      <ButtonStandard
                        type="button"
                        buttonType="principal"
                        onClick={() => handleCalculate(values)}
                      >
                        {translation.productionOrder.form.buttonCalculate}
                      </ButtonStandard>
                    </Col>
                  </Row>
                )}

                {recipeReadiness != null && !recipeReadiness.ready && (
                  <Row style={{ marginTop: 8 }}>
                    <Alert
                      type="error"
                      showIcon
                      message={translation.productionOrder.readiness.title}
                      description={
                        <>
                          {recipeReadiness.ingredientsWithoutStockItem?.length >
                            0 && (
                            <div>
                              {translation.productionOrder.readiness.withoutStockItem.replace(
                                "{{names}}",
                                recipeReadiness.ingredientsWithoutStockItem.join(
                                  ", ",
                                ),
                              )}
                            </div>
                          )}
                          {recipeReadiness.ingredientsWithoutDryMatter?.length >
                            0 && (
                            <div>
                              {translation.productionOrder.readiness.withoutDryMatter.replace(
                                "{{names}}",
                                recipeReadiness.ingredientsWithoutDryMatter.join(
                                  ", ",
                                ),
                              )}
                            </div>
                          )}
                        </>
                      }
                    />
                  </Row>
                )}

                {warnings.map((warning, index) => (
                  <Row
                    key={warningKey(warning, index)}
                    style={{ marginTop: 8 }}
                  >
                    <Alert
                      type="warning"
                      showIcon
                      message={warningMessage(warning)}
                      description={
                        acknowledgedWarnings[warningKey(warning, index)] ? (
                          <span
                            style={{ color: "#684e94", fontWeight: "bold" }}
                          >
                            {translation.productionOrder.warnings.acknowledged}
                          </span>
                        ) : (
                          <ButtonStandard
                            type="button"
                            buttonType="secondary"
                            onClick={() =>
                              handleAcknowledgeWarning(warning, index)
                            }
                          >
                            {translation.productionOrder.warnings.acknowledge}
                          </ButtonStandard>
                        )
                      }
                    />
                  </Row>
                ))}

                {pendingWarnings > 0 && (
                  <Row style={{ marginTop: 8 }}>
                    <span style={{ color: "#D44C4C" }}>
                      {translation.productionOrder.warnings.pending.replace(
                        "{{count}}",
                        pendingWarnings,
                      )}
                    </span>
                  </Row>
                )}

                {summary != null && (
                  <>
                    <SectionTitle>
                      {translation.productionOrder.form.resultTitle}
                    </SectionTitle>
                    <ResultPanel>
                      <Row type="flex" gutter={8}>
                        {resultItem(
                          translation.productionOrder.form.numberOfBatches,
                          summary.numberOfBatches,
                        )}
                        {resultItem(
                          translation.productionOrder.form.dryMatterPerBatchKg,
                          `${numberMask(summary.dryMatterPerBatchKg)} kg`,
                        )}
                        {resultItem(
                          translation.productionOrder.form
                            .originalMatterPerBatchKg,
                          `${numberMask(summary.originalMatterPerBatchKg)} kg`,
                        )}
                        {resultItem(
                          translation.productionOrder.form.plannedDryMatterKg,
                          `${numberMask(summary.plannedDryMatterKg)} kg`,
                        )}
                        {resultItem(
                          translation.productionOrder.form
                            .plannedOriginalMatterKg,
                          `${numberMask(summary.plannedOriginalMatterKg)} kg`,
                        )}
                        {resultItem(
                          translation.productionOrder.form.inputCost,
                          numberMask(summary.inputCost, true, null, null, 5, 5),
                        )}
                        {resultItem(
                          translation.productionOrder.form.manufacturingCost,
                          numberMask(
                            summary.manufacturingCost,
                            true,
                            null,
                            null,
                            5,
                            2,
                          ),
                        )}
                        {resultItem(
                          translation.productionOrder.form.totalCost,
                          numberMask(summary.totalCost, true, null, null, 5, 5),
                        )}
                        {resultItem(
                          translation.productionOrder.form.costPerKgDryMatter,
                          numberMask(
                            summary.costPerKgDryMatter,
                            true,
                            null,
                            null,
                            5,
                            2,
                          ),
                        )}
                      </Row>
                    </ResultPanel>
                  </>
                )}

                {items.length > 0 && (
                  <>
                    <SectionTitle>
                      {translation.productionOrder.form.itemsTitle}
                    </SectionTitle>
                    <Table
                      rowKey="supplementId"
                      size="small"
                      pagination={false}
                      dataSource={items}
                      style={{ width: "100%", marginTop: 8 }}
                    >
                      <Table.Column
                        title={
                          translation.productionOrder.form.items.supplementName
                        }
                        dataIndex="supplementName"
                        align="left"
                      />
                      <Table.Column
                        title={
                          translation.productionOrder.form.items
                            .dryMatterPercentage
                        }
                        dataIndex="dryMatterPercentage"
                        align="right"
                        render={(value) => numberMask(value)}
                      />
                      <Table.Column
                        title={
                          translation.productionOrder.form.items
                            .plannedDryMatterKg
                        }
                        dataIndex="plannedDryMatterKg"
                        align="right"
                        render={(value) => numberMask(value)}
                      />
                      <Table.Column
                        title={
                          translation.productionOrder.form.items
                            .plannedOriginalMatterKg
                        }
                        dataIndex="plannedOriginalMatterKg"
                        align="right"
                        render={(value) => numberMask(value)}
                      />
                      <Table.Column
                        title={translation.productionOrder.form.items.batch}
                        align="left"
                        render={(text, record) =>
                          readOnly ||
                          (record.batchOptions ?? []).length === 0 ? (
                            (record.batchNumber ??
                            translation.productionOrder.form.noBatch)
                          ) : (
                            <Select
                              style={{ minWidth: 180 }}
                              allowClear
                              value={
                                selectedBatches[record.supplementId] ||
                                undefined
                              }
                              placeholder={
                                translation.productionOrder.form.noBatch
                              }
                              onChange={(value) =>
                                handleChangeBatch(
                                  record.supplementId,
                                  value,
                                  values,
                                )
                              }
                            >
                              {record.batchOptions.map((option) => (
                                <Select.Option
                                  key={option.batchId}
                                  value={option.batchId}
                                >
                                  {batchLabel(option)}
                                </Select.Option>
                              ))}
                            </Select>
                          )
                        }
                      />
                      <Table.Column
                        title={
                          translation.productionOrder.form.items
                            .availableQuantity
                        }
                        dataIndex="availableQuantity"
                        align="right"
                        render={(value) =>
                          value == null ? "-" : numberMask(value)
                        }
                      />
                      <Table.Column
                        title={translation.productionOrder.form.items.unitCost}
                        dataIndex="unitCost"
                        align="right"
                        render={(value) =>
                          numberMask(value, true, null, null, 5, 5)
                        }
                      />
                      <Table.Column
                        title={translation.productionOrder.form.items.totalCost}
                        dataIndex="totalCost"
                        align="right"
                        render={(value) =>
                          numberMask(value, true, null, null, 5, 5)
                        }
                      />
                    </Table>
                  </>
                )}
              </Body>
              <Footer>
                <Row type="flex">
                  <Col span={24}>
                    <div className="buttonsDiv">
                      <ButtonStandard
                        type="button"
                        buttonType="type7"
                        onClick={onClose}
                      >
                        {readOnly
                          ? translation.buttons.close
                          : translation.buttons.cancel}
                      </ButtonStandard>
                      {!readOnly && (
                        <ButtonStandard
                          type="submit"
                          buttonType="type6"
                          disabled={calculation == null || pendingWarnings > 0}
                        >
                          {translation.buttons.save}
                        </ButtonStandard>
                      )}
                    </div>
                  </Col>
                </Row>
              </Footer>
            </form>
          </Spin>
        )}
      />
    </Container>
  );
};

export default DrawerProductionOrder;
